import { useCallback, useMemo } from "react";
import {
    FinancialDocument,
    FinancialDocumentExtended,
} from "@common/types/financialDocument";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { getReconciliationQueueAccountId } from "@common/helpers/financialDocument";
import { useOverlays } from "contexts/OverlayContext";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useSettings } from "@components/settings/SettingsProvider";
import { useGlobalTransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/useGlobalTransactionViewDrawer";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { EntitySettingsPages } from "@components/settings/constants";
import { getEntityPaneKey } from "@components/settings/lib";
import {
    DocumentFlowStatus,
    getTransactionMatchFilters,
    hasTransactionMatches,
} from "./lib";
import { DocumentStatusIconCallbacks } from "./DocumentStatusIcons";
import { useDocumentDetails } from "./useDocumentDetails";

export function useDocumentStatusNavigation(
    document: FinancialDocument | FinancialDocumentExtended,
): DocumentStatusIconCallbacks {
    const { openOverlay } = useOverlays();
    const navigate = useStateAwareNavigate();
    const { open: openSettings } = useSettings();
    const openTransactions = useGlobalTransactionViewDrawer();
    const { shownDocumentId, closeDetails } = useDocumentDetails();
    const { hasAccess: hasImporterAccess } = useAllowFeature(
        PREMIUM_FEATURES.IMPORTER,
    );
    const { hasAccess: hasReconciliationAccess } = useAllowFeature(
        PREMIUM_FEATURES.RECONCILIATION,
    );

    const transactionMatches = useMemo(
        () =>
            hasTransactionMatches(document)
                ? document.transactionMatches
                : null,
        [document],
    );

    const closeDetailsIfOpen = useCallback(() => {
        if (shownDocumentId) {
            closeDetails();
        }
    }, [shownDocumentId, closeDetails]);

    const onImportClick = useCallback(
        (status: NonNullable<DocumentFlowStatus>) => {
            if (
                status === "done" &&
                document.entityId &&
                document.financialAccountId
            ) {
                closeDetailsIfOpen();
                openSettings([
                    getEntityPaneKey(
                        EntitySettingsPages.SETTINGS,
                        document.entityId,
                    ),
                    document.financialAccountId.toString(),
                ]);
            } else if (status === "pending" && hasImporterAccess) {
                closeDetailsIfOpen();
                openOverlay("importer", true);
            }
        },
        [
            document.entityId,
            document.financialAccountId,
            hasImporterAccess,
            closeDetailsIfOpen,
            openSettings,
            openOverlay,
        ],
    );

    const reconciliationAccountId = document.importerQueue
        ? getReconciliationQueueAccountId(document.importerQueue)
        : null;

    const onReconciliationClick = useCallback(
        (status: NonNullable<DocumentFlowStatus>) => {
            if (!hasReconciliationAccess) {
                return;
            }

            if (status === "done") {
                closeDetailsIfOpen();
                navigate("/reconciliation");
            } else if (status === "pending" && reconciliationAccountId) {
                closeDetailsIfOpen();
                openOverlay("reconcilieFinancialAccount", {
                    financialAccountId: reconciliationAccountId,
                    onSuccess: () => navigate("/reconciliation"),
                });
            }
        },
        [
            navigate,
            reconciliationAccountId,
            closeDetailsIfOpen,
            openOverlay,
            hasReconciliationAccess,
        ],
    );

    const onMatchClick = useCallback(() => {
        if (!transactionMatches || transactionMatches.length === 0) {
            return;
        }

        closeDetailsIfOpen();
        openTransactions(getTransactionMatchFilters(transactionMatches));
    }, [transactionMatches, closeDetailsIfOpen, openTransactions]);

    return {
        onImportClick,
        onReconciliationClick,
        onMatchClick,
    };
}
